/**
 * GF Spam Hexer — Form Collector
 *
 * Thin orchestrator that wires together the collector modules.
 * Manages the lifecycle of spam protection signals for a single form:
 * 1. Starts PoW solving immediately (if enabled)
 * 2. Eagerly populates the hidden payload field as soon as the solution is ready
 * 3. Hooks into GF's pre-submission filter to refresh expired challenges
 *
 * Called by GF's init scripts (`ON_PAGE_RENDER`) which fire on every render,
 * including AJAX re-renders. The collector handles re-initialization by
 * destroying the old PoW manager and creating a new one with a fresh challenge.
 *
 * @module collector
 */

import type { PoWDebugStatus } from './pow-manager';
import type { GpshFrontendConfig } from '../frontend';
import {
	formConfigs,
	initializedFormNodes,
	powManagers,
	refreshingForms,
} from './state';
import { initPoW, createManager } from './init';
import { isOnLastPage } from './multi-page';
import {
	ensurePayloadField,
	getFormCopies,
	writePayloadField,
} from './payload';
import { bindSubmissionHook } from './submission-hook';
import { maybeExposeDebugApi } from './debug-api';
import { createLogger } from '@shared/logger';

const logger = createLogger('Collector');

/** Full debug status including collector-level state. */
export interface CollectorDebugStatus extends PoWDebugStatus {
	status?: 'no_manager';
	isRefreshing: boolean;
	isDeferredForPage: boolean;
}

let submissionHookBound = false;

/**
 * Whether this call is a repeat init for markup already being handled.
 *
 * The config cannot answer this: GF's `gform_post_render` handler is bound to
 * `document` and closes over the config PHP wrote at first page render, so an
 * AJAX re-render replays that same config, embedded challenge and all. The
 * markup does change — GF swaps the form element out — so element identity is
 * the discriminator. Records the current elements for the next call either way.
 */
function isRepeatInitForSameMarkup(formId: number): boolean {
	const current = getFormCopies(formId);
	const previous = initializedFormNodes.get(formId);

	initializedFormNodes.set(formId, current);

	return (
		previous !== undefined &&
		previous.length === current.length &&
		previous.every((node, index) => node === current[index])
	);
}

/**
 * Initializes the collector for a single form.
 *
 * Called from GF init scripts on every `ON_PAGE_RENDER` event, and on DOM ready
 * for every form PHP published a config for. A first call starts PoW solving;
 * an AJAX re-render restarts it; a repeat call for markup already being handled
 * keeps the in-flight solve, so a page load still fetches one challenge.
 *
 * @param config Per-form config from PHP (via `GFFormDisplay::add_init_script`).
 */
export async function initCollector(config: GpshFrontendConfig): Promise<void> {
	const { formId } = config;

	logger.group(`initCollector — formId: ${formId}`, () => {
		logger.log('config:', config);
		logger.log('powEnabled:', config.powEnabled);
		logger.log('powChallenge:', config.powChallenge ?? '(none)');
		logger.log('payloadField:', config.payloadField);
		logger.log('workerUrl:', config.workerUrl);
	});

	// Store config for later use by the submission hook.
	formConfigs.set(formId, config);

	const isRepeat = isRepeatInitForSameMarkup(formId);
	const existing = powManagers.get(formId);

	// Ensure the hidden payload field exists (may need re-creation after AJAX).
	ensurePayloadField(formId, config.payloadField);

	if (isRepeat && existing) {
		logger.log(
			`formId ${formId} — repeat init for the same markup, keeping existing PoWManager`
		);

		// A copy may have gained its field only just now; put the solution back.
		const solution = existing.getSolution();
		if (solution) {
			writePayloadField(formId, solution, config.payloadField);
		}
	} else {
		// Re-initialize PoW (fresh challenge on every render).
		initPoW(formId, config);
	}

	// Bind the submission hook on first collector init (deferred from module
	// load to ensure window.gform.utils is available).
	if (!submissionHookBound) {
		submissionHookBound = bindSubmissionHook();
	}
}

// ── Debug API ───────────────────────────────────────────────────────

maybeExposeDebugApi({
	powManagers,
	formConfigs,
	refreshingForms,
	isOnLastPage,
	initPoW,
	writePayloadField,
	createManager,
});
